import {
  useMutation,
  useQueryClient,
  type QueryKey
} from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import type { BulkPayload } from '@/types/bulk-action';
import { discoveredFieldsApi } from './discovered-fields.api';
import { discoveredFieldKeys } from './discovered-fields.queries';
import { ocrTemplateKeys } from './ocr-templates.queries';
import type {
  ApproveDiscoveredFieldRequest,
  DiscoveredFieldStatus
} from '../types';

/**
 * Every cache root a decision changes.
 *
 * Approving a discovered field WRITES A CUSTOM FIELD onto the template, so the
 * template's own caches go stale too — the configure screen's field list and
 * the listing's field-count badge both render from `ocrTemplateKeys`, a
 * different root from the review queue's. Invalidating only the queue leaves
 * the approved field invisible on the template until a hard reload.
 *
 * Exported and pinned by a unit test rather than left as a comment, because
 * "which roots render this row" is exactly the thing that is easy to forget
 * when a new surface starts reading it.
 */
export function discoveredFieldDecisionInvalidationKeys(): QueryKey[] {
  return [discoveredFieldKeys.all, ocrTemplateKeys.all];
}

function useDecisionInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    for (const queryKey of discoveredFieldDecisionInvalidationKeys()) {
      queryClient.invalidateQueries({ queryKey });
    }
  };
}

export function useApproveDiscoveredFieldMutation() {
  const t = useTranslations('discoveredFields');
  const invalidate = useDecisionInvalidation();

  return useMutation({
    mutationFn: ({
      id,
      data
    }: {
      id: number;
      data?: ApproveDiscoveredFieldRequest;
    }) => discoveredFieldsApi.approve(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('approveSuccess')
    },
    onSuccess: invalidate
  });
}

export function useRejectDiscoveredFieldMutation() {
  const t = useTranslations('discoveredFields');
  const invalidate = useDecisionInvalidation();

  return useMutation({
    mutationFn: ({
      id,
      reason,
      matchThreshold
    }: {
      id: number;
      reason?: string | null;
      /** 0-100, or null/undefined to dismiss only this exact field. */
      matchThreshold?: number | null;
    }) =>
      discoveredFieldsApi.reject(id, {
        rejection_reason: reason,
        match_threshold: matchThreshold
      }),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('rejectSuccess')
    },
    onSuccess: invalidate
  });
}

/**
 * Change the radius of a rejection that already stands (OMA-634).
 *
 * Same endpoint as the verdict — re-deciding a key with a different percentage
 * IS what `reject` does, and it is what writes the audit entry recording which
 * radius was in force for the runs in between — but a different ACT, so it
 * says so: the global success toast would otherwise announce "field rejected"
 * to someone who only moved a slider on a field that was already dismissed.
 * The existing reason is passed back in by the caller so the edit cannot erase
 * it.
 */
export function useUpdateRejectionRadiusMutation() {
  const t = useTranslations('discoveredFields');
  const invalidate = useDecisionInvalidation();

  return useMutation({
    mutationFn: ({
      id,
      reason,
      matchThreshold
    }: {
      id: number;
      reason?: string | null;
      /** 0-100, or null to fall back to the template's own percentage. */
      matchThreshold: number | null;
    }) =>
      discoveredFieldsApi.reject(id, {
        rejection_reason: reason,
        match_threshold: matchThreshold
      }),
    meta: {
      operationType: 'update',
      // The endpoint answers with the rejection message; this action is an
      // edit of one, so it raises its own.
      skipGlobalSuccessToast: true,
      fallbackSuccessMessage: t('rejectThresholdSaved')
    },
    onSuccess: () => {
      invalidate();
      toast.success(t('rejectThresholdSaved'));
    }
  });
}

export function useUpdateDiscoveredFieldDescriptionMutation() {
  const t = useTranslations('discoveredFields');
  const invalidate = useDecisionInvalidation();

  return useMutation({
    mutationFn: ({
      id,
      description
    }: {
      id: number;
      description: string | null;
    }) => discoveredFieldsApi.updateDescription(id, description),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('descriptionSaved')
    },
    // The same roots a verdict invalidates: the description rides the row, and
    // an approved field carries it onto the template's own custom field.
    onSuccess: invalidate
  });
}

export function useIgnoreDiscoveredFieldMutation() {
  const t = useTranslations('discoveredFields');
  const invalidate = useDecisionInvalidation();

  return useMutation({
    mutationFn: (id: number) => discoveredFieldsApi.ignore(id),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('ignoreSuccess')
    },
    // Ignoring an approved key revokes its template field, so the template
    // roots move too — the same set every verdict invalidates.
    onSuccess: invalidate
  });
}

export function useResetDiscoveredFieldMutation() {
  const t = useTranslations('discoveredFields');
  const invalidate = useDecisionInvalidation();

  return useMutation({
    mutationFn: (id: number) => discoveredFieldsApi.reset(id),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('resetSuccess')
    },
    onSuccess: invalidate
  });
}

export function useBulkDecideDiscoveredFieldsMutation() {
  const t = useTranslations('discoveredFields');
  const invalidate = useDecisionInvalidation();

  return useMutation({
    mutationFn: ({
      payload,
      status,
      reason,
      matchThreshold
    }: {
      payload: BulkPayload;
      status: Exclude<DiscoveredFieldStatus, 'pending'>;
      reason?: string | null;
      matchThreshold?: number | null;
    }) =>
      discoveredFieldsApi.bulkDecide(payload, status, reason, matchThreshold),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('bulkDecideSuccess')
    },
    onSuccess: invalidate
  });
}
